/**
 * VersionCard — one entry in the Workflow Versions timeline.
 *
 * Renders the node (left of the rail's vertical hairline) + a
 * collapsed card header. The header is a 4-column grid:
 *
 *   1. versionId — `v{n}`, monospace
 *   2. summaryLine — operation label + agent prefix + summary text
 *   3. metaCol — actor (avatar + display name) + relative time + HEAD pill
 *   4. rightActions — Restore button (gated) + chevron
 *
 * The body (rendered when `expanded === true`) is a per-operation
 * dispatcher introduced in Commits 4-10. For Commit 3 the body
 * area renders a small placeholder so the expand chevron has
 * something to reveal.
 *
 * Click anywhere on the header → calls `onToggleExpand`. Restore
 * stops event propagation so it doesn't also toggle expand.
 */
import React from 'react';
import { VersionRecord } from '../../state/types';
import {
  OPERATION_META,
  OperationTone,
  agentDisplayName,
} from '../../utils/operationMeta';
import formatRelativeTime from '../../../../utils/formatRelativeTime';
import { Badge } from '../../../../components/ui/Badge';
import type { BadgeColor } from '../../../../components/ui/Badge/Badge';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import BodyDispatcher from '../operation-bodies/BodyDispatcher';
import styles from './VersionCard.module.css';

// Button2 is a JSX forwardRef primitive; cast for use from TSX.
const ButtonAny = Button as unknown as React.ComponentType<any>;

// Operation tone → 2.0 Badge palette colour (soft action pill).
const TONE_BADGE_COLOR: Record<OperationTone, BadgeColor> = {
  info: 'blue',
  neutral: 'gray',
  success: 'green',
  warning: 'amber',
  error: 'red',
};

interface VersionCardProps {
  version: VersionRecord;
  isExpanded: boolean;
  /** version_number of the workflow's HEAD — drives the `HEAD` pill
   *  and gates the Restore button (latest can't be restored to
   *  itself). */
  currentVersionNumber: number;
  /** Detail fetch in flight for this version's body. */
  isLoadingDetail: boolean;
  onToggleExpand: (versionNumber: number) => void;
  onRequestRestore: (versionNumber: number) => void;
}

const initials = (name: string): string => {
  if (!name || name === 'system') return 'S';
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

export default function VersionCard({
  version,
  isExpanded,
  currentVersionNumber,
  isLoadingDetail,
  onToggleExpand,
  onRequestRestore,
}: VersionCardProps) {
  const meta = OPERATION_META[version.operation_type];
  const isLatest = version.version_number === currentVersionNumber;
  // Restore is disabled for the latest version (no-op), snapshots
  // (can't restore to a snapshot directly), and baselines (would
  // wipe the history). Matches legacy VersionsPanel gating.
  const canRestore =
    !isLatest &&
    version.operation_type !== 'snapshot' &&
    version.operation_type !== 'baseline';
  const agentLabel = version.agent_name
    ? agentDisplayName(version.agent_name)
    : null;

  const handleHeaderClick = () => onToggleExpand(version.version_number);
  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggleExpand(version.version_number);
    }
  };
  const handleRestoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRequestRestore(version.version_number);
  };

  return (
    <div className={styles.entry}>
      <div
        className={`${styles.card} ${
          version.operation_type === 'snapshot' ? styles.snapshot : ''
        }`}
      >
        <div
          className={styles.cardHeader}
          onClick={handleHeaderClick}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          onKeyDown={handleHeaderKeyDown}
        >
          <span className={styles.versionId}>v{version.version_number}</span>

          <span className={styles.summaryLine}>
            <Badge variant="soft" radius="full" color={TONE_BADGE_COLOR[meta.tone]}>
              {meta.label}
            </Badge>
            <span className={styles.summary}>
              {agentLabel && (
                <span className={styles.summaryAgent}>{agentLabel} —</span>
              )}
              {version.summary}
            </span>
          </span>

          <span className={styles.metaCol}>
            <span className={styles.actor}>
              <span className={styles.avatar}>
                {initials(version.user_display_name)}
              </span>
              {version.user_display_name}
            </span>
            <span className={styles.timestamp}>
              {formatRelativeTime(version.created_at)}
            </span>
            {isLatest && <Badge preset="default">HEAD</Badge>}
          </span>

          <span className={styles.rightActions}>
            {canRestore && (
              <ButtonAny
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRestoreClick}
              >
                Restore
              </ButtonAny>
            )}
            <Icon
              name="chevron-right"
              size={14}
              className={`${styles.chevron} ${
                isExpanded ? styles.chevronOpen : ''
              }`}
            />
          </span>
        </div>

        {isExpanded && (
          <div className={styles.detail}>
            {isLoadingDetail ? (
              <div className={styles.detailLoading}>Loading…</div>
            ) : (
              <BodyDispatcher version={version} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
